import { useState } from 'react';
import { Download } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogTrigger } from './ui/Dialog';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';
import { StepUpField } from './StepUpField';
import { apiDownload } from '../lib/api';
import { stepUpBody, stepUpCodeReady, stepUpErrorText } from '../lib/stepUp';
import { ApiError } from '../types/api';

function exportErrorText(err: unknown): string {
  return (
    stepUpErrorText(err) ??
    (err instanceof ApiError && err.status === 403
      ? 'Only an administrator can export contacts.'
      : err instanceof ApiError && err.body.message
        ? err.body.message
        : 'The contacts could not be exported. Please try again.')
  );
}

/** The bulk CSV export. Needs a fresh 2FA code (step-up), like deleting a contact. */
export function ExportContactsDialog({ filters }: { filters: Record<string, string> }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleExport() {
    setError(null);
    setExporting(true);
    try {
      await apiDownload('/contacts/export', 'contacts.csv', {
        body: { ...filters, ...stepUpBody(code) },
      });
      setOpen(false);
      setCode('');
    } catch (err) {
      setError(exportErrorText(err));
    } finally {
      setExporting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setCode('');
          setError(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="secondary" className="ml-auto">
          <Download size={15} aria-hidden="true" />
          Export CSV
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Export contacts"
        description="The file contains personal data. Enter a fresh code to confirm. The export is logged."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (stepUpCodeReady(code) && !exporting) void handleExport();
          }}
        >
          {error ? <Alert>{error}</Alert> : null}
          <StepUpField id="export-code" value={code} onChange={setCode} />
          <DialogFooter>
            <DialogClose>
              <Button type="button" variant="secondary" className="h-11">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="primary"
              disabled={!stepUpCodeReady(code) || exporting}
              className="h-11"
            >
              {exporting ? 'Preparing…' : 'Download CSV'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
